import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { FinancialSettlementAdvancePayment } from '@dreryk/egabinet-financial-settlement-frontend-api';

import { Bean, MoneyPatternType, ValidationRules } from '@dreryk/react-common';
import { MoneyInput, StyledMaxWidth } from '@dreryk/react-components';

import { visitSettlementTranslationKeys } from '../../../translations';
import { useMedicalServicesPrices } from '../../medicalServices/prices/MedicalServicesPrices.hooks';
import { VisitSettlementFormController } from '../VisitSettlementForm';
import { areAdvancePaymentsAvailable } from '../VisitSettlementForm.utils';

import { getMinValueForPaymentAmount } from './payment.utils';

const t = visitSettlementTranslationKeys.shared.form.payment;

type PaymentAmountInputProps = Pick<VisitSettlementFormController, 'register' | 'trigger'> & {
   readonly medicalServicesPrices: number[];
   readonly advancePayments?: FinancialSettlementAdvancePayment[];
};

const getInputLabel = (advancePayments?: FinancialSettlementAdvancePayment[]): string => {
   return areAdvancePaymentsAvailable(advancePayments) ? t.amountAccepted.label : t.amountInput.label;
};

export const PaymentAmountInput = (props: PaymentAmountInputProps): JSX.Element => {
   const { amountDue, advancePaymentsAmount } = useMedicalServicesPrices({
      advancePayments: props.advancePayments,
      medicalServicesPrices: props.medicalServicesPrices,
   });

   const validationRules = useMemo<ValidationRules<Bean>>(() => {
      return { required: true };
   }, []);

   useUpdateEffect(() => {
      props.trigger('payment.amount');
   }, [amountDue]);

   return (
      <StyledMaxWidth maxWidth={133}>
         <MoneyInput
            testId={'paymentAmount'}
            label={getInputLabel(props.advancePayments)}
            controller={props.register('payment.amount', validationRules)}
            pattern={MoneyPatternType.POSITIVE}
            min={getMinValueForPaymentAmount(amountDue, advancePaymentsAmount)}
            max={999999999.99}
            nowrap
         />
      </StyledMaxWidth>
   );
};
